/*
 * One strategy: what it is, what it trades, how it would have done, and the
 * one control that makes it real.
 *
 * MOVING TO LIVE IS DELIBERATELY AWKWARD. It is a separate dialog, it names
 * the amount at risk in rupees rather than in percentages, and it cannot be
 * reached by mistake from the list. Everything else on this page can be
 * changed freely, because everything else is reversible.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  useAddWatch,
  useBacktest,
  useCandles,
  useDeleteStrategy,
  useRemoveWatch,
  useSetEnabled,
  useStrategy,
  useUpdateStrategy,
} from "../api/hooks";
import { useWatch } from "../api/stream";
import type { BacktestResult, Interval } from "../api/types";
import { CandleChart } from "../charts/CandleChart";
import { IconChart, IconPlay, IconTrash, IconWarning } from "../components/Icons";
import { LiveChange, LivePrice } from "../components/LivePrice";
import { Modal } from "../components/Modal";
import { SymbolPicker } from "../components/SymbolPicker";
import { useToast } from "../components/Toast";
import {
  DAYS_FOR,
  KIND_BLURB,
  clockFromMs,
  pct,
  price as fmtPrice,
  rupees,
} from "../lib/format";
import { StrategyForm, type FormState, formFrom, toPayload } from "./StrategyForm";

export function StrategyPage() {
  const { id } = useParams();
  const strategy = useStrategy(id);
  const navigate = useNavigate();
  const toast = useToast();

  const setEnabled = useSetEnabled();
  const remove = useDeleteStrategy();
  const addWatch = useAddWatch();
  const removeWatch = useRemoveWatch();

  const [editing, setEditing] = useState(false);
  const [chartToken, setChartToken] = useState<number | null>(null);

  const s = strategy.data;
  const tokens = useMemo(() => s?.watches.map((w) => w.instrumentToken) ?? [], [s]);
  useWatch(tokens);

  useEffect(() => {
    // The first instrument is charted by default; a page with a chart slot and
    // nothing in it reads as broken.
    if (chartToken === null && tokens.length > 0) setChartToken(tokens[0]);
  }, [tokens, chartToken]);

  useEffect(() => {
    // The browser's own jump to #instruments happens before this page has
    // fetched anything, when the target is still a spinner and the element
    // does not exist — so it silently does nothing and the link looks broken.
    // Once the strategy is in, scroll there properly.
    if (!strategy.data) return;
    const target = window.location.hash.slice(1);
    if (!target) return;
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [strategy.data]);

  if (strategy.isPending) {
    return <div className="card"><div className="empty"><span className="spinner" /></div></div>;
  }
  if (!s) {
    return (
      <div className="card">
        <div className="empty">
          <strong>Not found</strong>
          That strategy does not exist, or belongs to somebody else.
          <div className="mt"><Link className="btn sm" to="/strategies">Back</Link></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{s.name}</h1>
          <p>{KIND_BLURB[s.kind]}</p>
        </div>
        <div className="page-actions">
          <span className={`pill ${s.mode}`}>{s.mode}</span>
          <button
            className={`btn ${s.isEnabled ? "" : "success"}`}
            disabled={setEnabled.isPending}
            onClick={() =>
              setEnabled.mutate(
                { id: s.id, isEnabled: !s.isEnabled },
                {
                  onSuccess: () => toast.ok(s.isEnabled ? "Switched off." : "Running."),
                  onError: (e) => toast.fail(e),
                },
              )
            }
          >
            <IconPlay size={14} /> {s.isEnabled ? "Switch off" : "Switch on"}
          </button>
          <button className="btn" onClick={() => setEditing(true)}>Edit</button>
        </div>
      </div>

      {s.lastError && (
        <div className="banner warn">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>Stopped</strong>
            <p>{s.lastError}</p>
          </div>
        </div>
      )}

      {s.mode === "live" ? (
        <div className="banner danger">
          <span className="ico"><IconWarning size={17} /></span>
          <div className="body">
            <strong>This strategy trades real money</strong>
            <p>
              Up to {rupees(s.perTradeRupees)} a trade, {s.maxPositions} positions at
              once, and it stops itself after {rupees(s.dailyLossRupees)} of realised
              losses in a day. Switch the whole account back to Paper in the header to
              stop that.
            </p>
          </div>
        </div>
      ) : (
        <div className="banner info">
          <div className="body">
            <strong>Simulated</strong>
            <p>
              Nothing this rule does reaches Zerodha, and its trade counts and loss
              limits are not enforced — those are for real money. Paper or real is a
              property of the whole account now, switched in the header rather than
              set on each strategy.
            </p>
          </div>
        </div>
      )}

      <div className="grid main-side">
        <div className="col">
          <div className="card">
            <div className="card-head">
              <h2>Chart</h2>
              {chartToken && (
                <span className="sub">
                  <LivePrice token={chartToken} />{" "}
                  <LiveChange token={chartToken} />
                </span>
              )}
            </div>
            {tokens.length === 0 ? (
              <div className="empty">
                <strong>No instruments yet</strong>
                Add one below and it will be charted here.
              </div>
            ) : (
              <>
                <div className="chart-toolbar">
                  <div className="chip-group">
                    {s.watches.slice(0, 8).map((w) => (
                      <button
                        key={w.instrumentToken}
                        className={chartToken === w.instrumentToken ? "on" : ""}
                        onClick={() => setChartToken(w.instrumentToken)}
                      >
                        {w.tradingsymbol}
                      </button>
                    ))}
                  </div>
                </div>
                {chartToken && <ChartFor token={chartToken} interval={s.interval} />}
              </>
            )}
          </div>

          <div id="backtest">
            <Backtester strategyId={s.id} watches={s.watches} />
          </div>
        </div>

        <div className="col">
          <div className="card" id="instruments">
            <div className="card-head">
              <h2>Instruments</h2>
              <span className="sub">{s.watches.length}</span>
            </div>
            <div className="card-body col" style={{ gap: 10 }}>
              <SymbolPicker
                exclude={tokens}
                onPick={(i) =>
                  addWatch.mutate(
                    { id: s.id, instrumentToken: i.instrumentToken },
                    {
                      onSuccess: () => toast.ok(`${i.tradingsymbol} added.`),
                      onError: (e) => toast.fail(e),
                    },
                  )
                }
              />
              {s.watches.length === 0 ? (
                <p className="small faint" style={{ margin: 0 }}>
                  A strategy with no instruments does nothing at all.
                </p>
              ) : (
                <div className="col" style={{ gap: 5 }}>
                  {s.watches.map((w) => (
                    <div
                      key={w.instrumentToken}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 9,
                        padding: "7px 9px",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius-sm)",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{w.tradingsymbol}</div>
                        <div className="tiny faint">{w.exchange}</div>
                      </div>
                      <div className="right">
                        <div className="num small"><LivePrice token={w.instrumentToken} /></div>
                        <div className="tiny"><LiveChange token={w.instrumentToken} /></div>
                      </div>
                      <button
                        className="icon-btn"
                        aria-label={`Remove ${w.tradingsymbol}`}
                        onClick={() =>
                          removeWatch.mutate(
                            { id: s.id, instrumentToken: w.instrumentToken },
                            { onError: (e) => toast.fail(e) },
                          )
                        }
                      >
                        <IconTrash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head"><h2>Limits</h2></div>
            <div className="card-body col" style={{ gap: 0 }}>
              <Row k="Per trade" v={rupees(s.perTradeRupees)} />
              <Row k="Most positions" v={String(s.maxPositions)} />
              <Row k="Most trades a day" v={String(s.maxTradesPerDay)} />
              <Row k="Stops for the day at" v={rupees(s.dailyLossRupees)} />
              <Row k="Stop loss" v={s.stopLossPct ? `${s.stopLossPct}%` : "none"} />
              <Row k="Target" v={s.targetPct ? `${s.targetPct}%` : "none"} />
              <Row k="Trailing stop" v={s.trailingPct ? `${s.trailingPct}%` : "none"} />
              <Row k="Entry window" v={`${s.entryFrom} – ${s.entryTo}`} />
              <Row k="Square off" v={s.squareOffAt ?? "—"} />
              <Row k="Candle" v={s.interval} />
              <Row k="Product" v={s.product} />
            </div>
          </div>

          {s.kind === "webhook" && (
            <div className="card">
              <div className="card-head"><h2>Webhook</h2></div>
              <div className="card-body">
                <p className="small muted" style={{ marginTop: 0 }}>
                  Post signals to this URL with the secret you set. Every limit above
                  still applies to a signal that arrives this way.
                </p>
                <code
                  className="mono small"
                  style={{
                    display: "block",
                    padding: 9,
                    background: "var(--bg)",
                    border: "1px solid var(--border)",
                    borderRadius: 6,
                    wordBreak: "break-all",
                  }}
                >
                  POST {window.location.origin}/api/trade/webhook/{s.id}
                </code>
              </div>
            </div>
          )}

          <button
            className="btn danger block"
            onClick={() => {
              if (!window.confirm(`Delete “${s.name}”? Its history goes with it.`)) return;
              remove.mutate(s.id, {
                onSuccess: () => {
                  toast.ok("Deleted.");
                  navigate("/strategies");
                },
                onError: (e) => toast.fail(e),
              });
            }}
          >
            <IconTrash size={15} /> Delete this strategy
          </button>
        </div>
      </div>

      {editing && (
        <EditDialog
          strategy={s}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="switch-row">
      <div className="t" style={{ fontWeight: 400, color: "var(--text-dim)" }}>{k}</div>
      <div className="num" style={{ fontWeight: 600, fontSize: 13 }}>{v}</div>
    </div>
  );
}

/** Kept out of the page body so a failed candle fetch does not blank the page. */
function ChartFor({ token, interval }: { token: number; interval: Interval }) {
  const candles = useCandles(token, interval, DAYS_FOR[interval]);

  if (candles.isPending) {
    return <div className="empty" style={{ height: 420 }}><span className="spinner" /></div>;
  }
  if (candles.isError) {
    return (
      <div className="empty" style={{ height: 420 }}>
        <strong>No candles</strong>
        {String((candles.error as Error).message)}
      </div>
    );
  }
  return <CandleChart token={token} bars={candles.data.bars} interval={interval} />;
}

/* ─────────────────────────── backtest ─────────────────────────── */

function Backtester({
  strategyId,
  watches,
}: {
  strategyId: string;
  watches: { instrumentToken: number; tradingsymbol: string }[];
}) {
  const [token, setToken] = useState<number | null>(null);
  const [days, setDays] = useState(60);
  const [result, setResult] = useState<BacktestResult | null>(null);
  const run = useBacktest();
  const toast = useToast();

  useEffect(() => {
    if (token === null && watches.length > 0) setToken(watches[0].instrumentToken);
  }, [watches, token]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Backtest</h2>
        <span className="sub">On closed candles, gross of costs</span>
      </div>
      <div className="card-body col">
        <div className="row">
          <select
            value={token ?? ""}
            onChange={(e) => setToken(Number(e.target.value))}
            style={{ maxWidth: 220 }}
            disabled={watches.length === 0}
          >
            {watches.map((w) => (
              <option key={w.instrumentToken} value={w.instrumentToken}>
                {w.tradingsymbol}
              </option>
            ))}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ maxWidth: 150 }}>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={120}>120 days</option>
            <option value={250}>250 days</option>
          </select>
          <button
            className="btn"
            disabled={!token || run.isPending}
            onClick={() =>
              run.mutate(
                { id: strategyId, instrumentToken: token!, days },
                { onSuccess: setResult, onError: (e) => toast.fail(e) },
              )
            }
          >
            {run.isPending ? <span className="spinner" /> : <IconChart size={14} />} Run
          </button>
        </div>

        {watches.length === 0 && (
          <p className="small faint" style={{ margin: 0 }}>Add an instrument first.</p>
        )}

        {result && (
          <>
            <div className="grid cols-4">
              <div className="stat">
                <span className="k">Return</span>
                <span className={`v ${result.totalReturnPct >= 0 ? "up" : "down"}`}>
                  {pct(result.totalReturnPct)}
                </span>
                <span className="note">over {result.bars} bars</span>
              </div>
              <div className="stat">
                <span className="k">Trades</span>
                <span className="v">{result.trades.length}</span>
                <span className="note">{result.winRatePct}% won</span>
              </div>
              <div className="stat">
                <span className="k">Worst drawdown</span>
                <span className="v down">{pct(-Math.abs(result.maxDrawdownPct))}</span>
              </div>
              <div className="stat">
                <span className="k">Best / worst trade</span>
                <span className="v" style={{ fontSize: 17 }}>
                  <span className="up">{pct(result.bestPct)}</span>{" "}
                  <span className="faint">/</span>{" "}
                  <span className="down">{pct(result.worstPct)}</span>
                </span>
              </div>
            </div>

            <p className="tiny faint" style={{ margin: 0 }}>{result.note}</p>

            {result.trades.length > 0 && (
              <div className="table-wrap" style={{ maxHeight: 260, overflowY: "auto" }}>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Entered</th>
                      <th className="num">In</th>
                      <th>Exited</th>
                      <th className="num">Out</th>
                      <th className="num">Return</th>
                      <th>Why</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.trades.map((t, i) => (
                      <tr key={i}>
                        <td className="small faint">{clockFromMs(t.entryMs)}</td>
                        <td className="num">{fmtPrice(t.entryPrice)}</td>
                        <td className="small faint">{clockFromMs(t.exitMs)}</td>
                        <td className="num">{fmtPrice(t.exitPrice)}</td>
                        <td className={`num ${t.returnPct >= 0 ? "up" : "down"}`}>
                          {pct(t.returnPct)}
                        </td>
                        <td className="small faint">{t.exitReason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────── dialogs ─────────────────────────── */

function EditDialog({
  strategy,
  onClose,
}: {
  strategy: Parameters<typeof formFrom>[0] & { id: string; kind: never | string };
  onClose: () => void;
}) {
  const [form, setForm] = useState<FormState>(() => formFrom(strategy));
  const update = useUpdateStrategy();
  const toast = useToast();

  return (
    <Modal
      title="Edit strategy"
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn primary"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { id: strategy.id, body: toPayload(form) },
                {
                  onSuccess: () => {
                    toast.ok("Saved.");
                    onClose();
                  },
                  onError: (e) => toast.fail(e),
                },
              )
            }
          >
            {update.isPending ? <span className="spinner" /> : "Save"}
          </button>
        </>
      }
    >
      <StrategyForm value={form} onChange={setForm} lockKind />
    </Modal>
  );
}

